import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    Pagination,
    Stack,
} from '@mui/material';

import { useAppContext } from '../context/AppContext';

const getProfileId = (row) => row?.id || row?.profile_id || row?.profile;

const RAProfiles = () => {
    const navigate = useNavigate();
    const { getRAProfiles, raProfiles, deleteRAProfile } = useAppContext();
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const [filters, setFilters] = useState({
        profile: '',
        profile_category: '',
        country: '',
    });
    const [activeFilters, setActiveFilters] = useState({
        profile: '',
        profile_category: '',
        country: '',
    });

    useEffect(() => {
        getRAProfiles(currentPage, pageSize, activeFilters);
    }, [currentPage, activeFilters]);

    const handleCreate = () => {
        navigate('/ra-profiles/create');
    };

    const handleFilterChange = (name) => (event) => {
        setFilters((prev) => ({ ...prev, [name]: event.target.value }));
    };

    const handleSearch = () => {
        setCurrentPage(1);
        setActiveFilters(filters);
    };

    const handleClearFilters = () => {
        const cleared = { profile: '', profile_category: '', country: '' };
        setFilters(cleared);
        setCurrentPage(1);
        setActiveFilters(cleared);
    };

    const handleEdit = (raProfile) => {
        const profileId = getProfileId(raProfile);
        if (!profileId) {
            alert('Unable to identify selected RA profile');
            return;
        }
        navigate(`/ra-profiles/${encodeURIComponent(profileId)}/update`);
    };

    const handleDelete = async (raProfile) => {
        const profileId = getProfileId(raProfile);
        if (!profileId) {
            alert('Unable to identify selected RA profile');
            return;
        }

        const confirmDelete = window.confirm(`Delete RA Profile "${raProfile.profile}"?`);
        if (!confirmDelete) return;

        try {
            await deleteRAProfile(profileId);
            await getRAProfiles(currentPage, pageSize, activeFilters);
        } catch (err) {
            console.error('Error deleting RA profile:', err);
            alert('Error deleting RA profile');
        }
    };

    const rows = Array.isArray(raProfiles?.records) ? raProfiles.records : [];
    const totalRecords = Number(raProfiles?.total_records || rows.length || 0);
    const responsePageSize = Number(raProfiles?.page_size || pageSize);
    const totalPages = Math.max(1, Math.ceil(totalRecords / responsePageSize));

    return (
        <>
            <Typography variant="h5">RA Profiles</Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ my: 2 }}>
                <TextField
                    label="Profile"
                    value={filters.profile}
                    onChange={handleFilterChange('profile')}
                />
                <TextField
                    label="Category"
                    value={filters.profile_category}
                    onChange={handleFilterChange('profile_category')}
                />
                <TextField
                    label="Country"
                    value={filters.country}
                    onChange={handleFilterChange('country')}
                />
                <Button variant="outlined" onClick={handleSearch}>
                    Search
                </Button>
                <Button variant="outlined" onClick={handleClearFilters}>
                    Clear
                </Button>
                <Button variant="contained" onClick={handleCreate}>
                    Add New
                </Button>
            </Stack>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Profile</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>PKI Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((raProfile) => (
                            <TableRow key={getProfileId(raProfile)}>
                                <TableCell>{raProfile.profile}</TableCell>
                                <TableCell>{raProfile.profile_category}</TableCell>
                                <TableCell>{raProfile.country}</TableCell>
                                <TableCell>{raProfile.pki_name}</TableCell>
                                <TableCell>{raProfile.description}</TableCell>
                                <TableCell>
                                    <Button variant="outlined" onClick={() => handleEdit(raProfile)} sx={{ mr: 1 }}>
                                        Modify
                                    </Button>
                                    <Button variant="outlined" color="error" onClick={() => handleDelete(raProfile)}>
                                        Delete
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                sx={{
                    mt: 2, display: 'flex', justifyContent: 'center',
                    '& .MuiPaginationItem-root': {
                        color: 'white',
                        backgroundColor: 'secondary.main',
                        '&.Mui-selected': {
                            backgroundColor: 'secondary.dark',
                        },
                        '&:hover': {
                            backgroundColor: 'secondary.light',
                        },
                    },
                }}
            />
        </>
    );
};

export default RAProfiles;
